import type { FormikHelpers } from "formik";
import type {
  frequenciesAll_frequenciesAll as SubscriptionFrequencyRow,
  pricingAllForSubscriptions_pricingAll as SubscriptionPricingRow,
} from "types/gql-types";
import type { SubscriptionForm } from "./form";

// --- GraphQL validation message → Formik field errors ---

type SubscriptionFormField =
  | "propertyId"
  | "interestListId"
  | "startDate"
  | "endDate"
  | "renewalDate";

const VALIDATION_FIELD_SEGMENT =
  /^(?<fieldSnake>[a-z_]+)\s+(?<msg>.+)$/;

const validationFieldToFormKey = new Map<string, SubscriptionFormField>([
  ["start_date", "startDate"],
  ["end_date", "endDate"],
  ["renewal_date", "renewalDate"],
  ["property_interest_list_id", "interestListId"],
]);

/**
 * Parses backend subscription changeset errors serialized as
 * "Validation failed: field_name message; field_name2 message2".
 * Maps snake_case GraphQL/Ecto fields to Formik field names.
 */
export const parseSubscriptionValidationMessage = function (
  message: string,
): Partial<Record<SubscriptionFormField, string>> {
  const prefix = "Validation failed: ";
  if (!message.startsWith(prefix)) {
    return {};
  }

  const rest = message.slice(prefix.length);
  const segments = rest
    .split("; ")
    .map((segment) => segment.trim())
    .filter((segment) => segment.length > 0);

  const out: Partial<Record<SubscriptionFormField, string>> = {};

  for (const segment of segments) {
    const match = VALIDATION_FIELD_SEGMENT.exec(segment);
    const groups = match?.groups;
    if (groups) {
      const { fieldSnake, msg } = groups;
      if (fieldSnake && msg) {
        const formKey = validationFieldToFormKey.get(fieldSnake);
        if (formKey) {
          out[formKey] = msg;
        }
      }
    }
  }

  return out;
};

// --- Property / interest list select labels ---

type SubscriptionEntityOptionLabelInput = {
  name: string;
  organization?: { name: string } | null;
};

const subscriptionEntityOptionLabel = function (
  entity: SubscriptionEntityOptionLabelInput,
): string {
  const orgName = entity.organization?.name;
  return orgName ? `${entity.name} (${orgName})` : entity.name;
};

/** Label for a property in subscription selects. */
export const propertyLabel = subscriptionEntityOptionLabel;

/** Label for an interest list in subscription selects. */
export const interestListLabel = subscriptionEntityOptionLabel;

// --- Dates, select options, mutation helpers ---

/** Calendar date → `YYYY-MM-DD` for subscription GraphQL mutations. */
export const subscriptionFormDateToString = function (
  date: Date | null | undefined,
): string | undefined {
  if (date instanceof Date && !isNaN(date.getTime())) {
    return date.toISOString().split("T")[0];
  }
  // eslint-disable-next-line no-undefined -- optional mutation field omitted when unset
  return undefined;
};

/** `YYYY-MM-DD` from API → local calendar `Date` for the form. */
export const subscriptionFormDateFromString = function (
  dateString: string | null | undefined,
): Date | null {
  if (!dateString) {
    return null;
  }
  const parts = dateString.split("-").map(Number);
  if (parts.length !== 3) {
    return null;
  }
  const [year, month, day] = parts;
  const date = new Date(year, month - 1, day);
  return isNaN(date.getTime()) ? null : date;
};

export const formatSubscriptionPricingOptions = function (
  pricingList: SubscriptionPricingRow[],
): { value: string; label: string }[] {
  return pricingList.map(
    ({ id, pricingType, minimumPrice, unitThreshold, monthlyPricePerUnit }) => ({
      value: id,
      label: `${pricingType?.name ?? "Unknown"} - $${minimumPrice ?? "0"}${
        unitThreshold ? ` (${unitThreshold} units)` : ""
      }${monthlyPricePerUnit === null ? "" : ` · $${monthlyPricePerUnit}/unit`}`,
    }),
  );
};

export const formatSubscriptionFrequencyOptions = function (
  frequencies: SubscriptionFrequencyRow[],
): { value: number; label: string }[] {
  return frequencies.map(({ id, name }) => ({
    value: id,
    label: name ?? "",
  }));
};

export const formatSubscriptionPlanOptions = function (
  plans: { id: string; name: string }[],
): { value: string; label: string }[] {
  return plans.map(({ id, name }) => ({ value: id, label: name }));
};

/** Ensures the current entity appears in the list when missing from the filtered query. */
export const withSubscriptionSelectOptionIfMissing = function (
  options: { value: string; label: string }[],
  currentId: string | undefined,
  label: string | undefined,
): { value: string; label: string }[] {
  if (
    !currentId ||
    !label ||
    options.some((option) => option.value === currentId)
  ) {
    return options;
  }
  return [...options, { value: currentId, label }];
};

/**
 * Maps a GraphQL validation message to Formik field errors when parsable.
 * @returns true when inline field errors were applied.
 */
export const applySubscriptionGraphqlFieldErrors = function (
  message: string,
  helpers: Pick<FormikHelpers<SubscriptionForm>, "setErrors" | "setTouched">,
): boolean {
  const fieldErrors = parseSubscriptionValidationMessage(message);
  const keys = Object.keys(fieldErrors);
  if (keys.length === 0) {
    return false;
  }
  helpers.setErrors(fieldErrors);
  const touched = Object.fromEntries(
    keys.map((fieldKey) => [fieldKey, true]),
  ) as Record<string, boolean>;
  helpers.setTouched(touched, true);
  return true;
};
